/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import t from 'react-translate';

import { AngularContext } from 'react-app';
import { useAppDispatch } from 'redux/store';

import { addAlertMessage } from 'redux/actions/alert-messages';
import { AlertMessageType } from 'redux/schemas/app/alert-message';

import { config } from '@config/pendo.config.json';
import SectionContent from '../section-content';
import { DisableProps } from './integrations';

type MsCalendarIntegrationUserLevelProps = {
  onDisable: (props: DisableProps) => void;
};

const MsCalendarIntegrationUserLevel = (props: MsCalendarIntegrationUserLevelProps) => {
  const { onDisable } = props;
  const translationBase = t.INSTITUTIONS.ADVANCED_SETTINGS.MICROSOFT_CALENDAR_USER_LEVEL;
  const { injectServices, $scope } = useContext(AngularContext);
  const [InstitutionsManager] = injectServices(['InstitutionsManager']);
  const [hasIntegration, setHasIntegration] = useState(
    $scope.$eval(
      'vm.InstitutionsManager.institution.hasMs365CalendarIntegration',
    ),
  );

  const [isRequesting, setIsRequesting] = useState(false);
  const dispatch = useAppDispatch();

  useEffect(() => {
    $scope.$watch('vm.InstitutionsManager.institution.hasMs365CalendarIntegration', (newValue) => {
      setHasIntegration(newValue);
    });
  }, [$scope]);

  const enable = useCallback(() => {
    setIsRequesting(true);
    InstitutionsManager.toggleMsCalendarIntegration(true)
      .then(() => {
        setIsRequesting(false);
        InstitutionsManager.institution.updateFromReact({
          hasMs365CalendarIntegration: true,
        });
        dispatch(
          addAlertMessage({
            type: AlertMessageType.SUCCESS,
            header: t.FORM.SUCCESS_BANG(),
            message: translationBase.ENABLE.SUCCESS(),
          }),
        );
      }).catch(() => {
        setIsRequesting(false);
        dispatch(
          addAlertMessage({
            type: AlertMessageType.ERROR,
            header: t.FORM.OOPS(),
            message: t.FORM.SOMETHING_WRONG(),
          }),
        );
      });
  }, [InstitutionsManager, dispatch]);

  const disable = useCallback(() => {
    setIsRequesting(true);
    onDisable({
      action: () => InstitutionsManager.toggleMsCalendarIntegration(false),
      onSuccess: () => {
        setIsRequesting(false);
        InstitutionsManager.institution.updateFromReact({
          hasMs365CalendarIntegration: false,
        });
      },
      successMessage: translationBase.DISABLE.SUCCESS(),
      title: translationBase.DISABLE.TITLE(),
      bodyText: translationBase.DISABLE.DESCRIPTION(),
      confirmDataQa: config.pendo.settings.msCalendarUserLevel.disableConfirm,
      cancelDataQa: config.pendo.settings.msCalendarUserLevel.disableCancel,
      forceSuccess: true,
    });
  }, [InstitutionsManager, onDisable]);

  const [ctaText, onAction] = useMemo(() => (
    hasIntegration
      ? [translationBase.DISABLE.BUTTON(), disable]
      : [translationBase.ENABLE.BUTTON(), enable]
  ), [disable, enable, hasIntegration]);

  return (
    <SectionContent
      header={translationBase.TITLE()}
      description={[translationBase.DESCRIPTION()]}
      showCTAButton
      ctaText={ctaText}
      onAction={onAction}
      buttonVariant={hasIntegration ? 'secondary' : 'primary'}
      dataQa={config.pendo.settings.msCalendarUserLevel.cta}
      disabled={isRequesting}
    />
  );
};

export default MsCalendarIntegrationUserLevel;
